import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { AuthService } from '@/lib/auth';
import { isSupabaseConfigured } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isConfigured: boolean;
}

interface AuthContextType extends AuthState {
  signUp: (email: string, password: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<any>;
  resetPassword: (email: string) => Promise<any>;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);

  // Always call hooks at the top level - never conditionally
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    isConfigured: isSupabaseConfigured()
  });

  const getInitialSession = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setAuthState({
        user: null,
        session: null,
        loading: false,
        isConfigured: false
      });
      return;
    }

    try {
      const authService = new AuthService();
      const session = await authService.getCurrentSession();
      const user = session?.user || null;

      setAuthState({
        user,
        session,
        loading: false,
        isConfigured: true
      });
    } catch (error) {
      console.error('Error getting initial session:', error);
      setAuthState({
        user: null,
        session: null,
        loading: false,
        isConfigured: true
      });
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    getInitialSession();

    if (!isSupabaseConfigured()) {
      return;
    }

    const authService = new AuthService();
    const { data: { subscription } } = authService.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        setAuthState(prev => ({
          ...prev,
          user: session?.user || null,
          session,
          loading: false
        }));
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, [getInitialSession]);

  const signUp = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set up your environment variables.');
    }
    const authService = new AuthService();
    return authService.signUp(email, password);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set up your environment variables.');
    }
    const authService = new AuthService();
    return authService.signIn(email, password);
  }, []);

  const signOut = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set up your environment variables.');
    }
    const authService = new AuthService();
    return authService.signOut();
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set up your environment variables.');
    }
    const authService = new AuthService();
    return authService.resetPassword(email);
  }, []);

  // If context is available, use it; otherwise use our default implementation
  if (context !== undefined) {
    return context;
  }

  return {
    ...authState,
    signUp,
    signIn,
    signOut,
    resetPassword,
    refresh: getInitialSession
  };
}